import { IInfluencerSettings } from 'fitbeat.models';
import { IDynamicLinkService } from 'fitbeat.notification-service';

export class DynamicLinkService implements IDynamicLinkService {
  private readonly firebaseShortLinkUrl: string;

  constructor(
    private readonly firebaseApiKey: string,
    private readonly dynamicLinkPrefix: string,
    private readonly androidBundleId: string,
    private readonly iosBundleId: string,
    private readonly iosAppStoreId: string,
    private readonly socialScreenshotGeneratorDomain: string,
    private readonly webAppUrl: string,
    private readonly platformUrl: string,
    private readonly fetch: any,
  ) {
    this.firebaseShortLinkUrl = 'https://firebasedynamiclinks.googleapis.com/v1/shortLinks?' +
      `key=${this.firebaseApiKey}`;
  }

  public async createShortlinkAppFallback(
    urlRedirect: string,
    influencerSettings?: IInfluencerSettings,
  ): Promise<string> {

    const { googlePlayAnalytics, influencerSocialMetaTagInfo } =
      this.analyticsTagsFromInfluencerSettings(influencerSettings);

    const options: IFirebaseDynamicLinkInfo = {
      suffix: {
        option: 'SHORT',
      },
      dynamicLinkInfo: {
        domainUriPrefix: this.dynamicLinkPrefix,
        link: urlRedirect,
        androidInfo: {
          androidPackageName: this.androidBundleId,
        },
        iosInfo: {
          iosBundleId: this.iosBundleId,
          iosAppStoreId: this.iosAppStoreId,
        },
        analyticsInfo: {
          ...googlePlayAnalytics,
        },
        socialMetaTagInfo: {
          ...influencerSocialMetaTagInfo,
        },
      },
    };

    const request = await this.fetch(this.firebaseShortLinkUrl, {
      method: 'POST',
      body: JSON.stringify(options),
    });
    const result = await request.json();
    return result.shortLink;
  }

  public async createShortlinkWebFallback(
    urlRedirect: string,
    title = '',
    description = '',
    socialImageLink?: string,
    influencerSettings?: IInfluencerSettings,
  ): Promise<string> {

    const { googlePlayAnalytics, influencerSocialMetaTagInfo } =
      this.analyticsTagsFromInfluencerSettings(influencerSettings);

    const options: IFirebaseDynamicLinkInfo = {
      suffix: {
        option: 'SHORT',
      },
      dynamicLinkInfo: {
        domainUriPrefix: this.socialScreenshotGeneratorDomain,
        link: urlRedirect,
        navigationInfo: {
          enableForcedRedirect: true,
        },
        socialMetaTagInfo: {
          socialTitle: title,
          socialDescription: description,
          socialImageLink,
          ...influencerSocialMetaTagInfo,
        },
        analyticsInfo: {
          ...googlePlayAnalytics,
        },
      },
    };

    const request = await this.fetch(this.firebaseShortLinkUrl, {
      method: 'POST',
      body: JSON.stringify(options),
    });
    const result = await request.json();
    return result.shortLink;
  }

  public async openAppWebFallback(
    urlRedirect: string,
    forceRedirect: boolean = false,
    influencerSettings?: IInfluencerSettings,
  ): Promise<string> {

    const { googlePlayAnalytics, influencerSocialMetaTagInfo } =
      this.analyticsTagsFromInfluencerSettings(influencerSettings);

    const options: IFirebaseDynamicLinkInfo = {
      suffix: {
        option: 'SHORT',
      },
      dynamicLinkInfo: {
        domainUriPrefix: this.dynamicLinkPrefix,
        link: urlRedirect,
        navigationInfo: {
          enableForcedRedirect: forceRedirect,
        },
        androidInfo: {
          androidPackageName: this.androidBundleId,
          androidFallbackLink: urlRedirect,
        },
        iosInfo: {
          iosBundleId: this.iosBundleId,
          iosFallbackLink: urlRedirect,
        },
        analyticsInfo: {
          ...googlePlayAnalytics,
        },
        socialMetaTagInfo: {
          ...influencerSocialMetaTagInfo,
        },
      },
    };

    const request = await this.fetch(this.firebaseShortLinkUrl, {
      method: 'POST',
      body: JSON.stringify(options),
    });
    const result = await request.json();
    return result.shortLink;
  }

  public async createContinueSignupLink(uid: string, continueSignupToken: string | undefined): Promise<string> {
    if (!continueSignupToken) {
      return '';
    }
    const url = `${this.webAppUrl}/AuthLoading?userId=${uid}&continueSignupToken=${continueSignupToken}`;

    return this.createShortlinkAppFallback(url, {
      coupon: '',
      socialMetaTags: {
        title: 'Continue your Fitillion app signup',
        description: 'Tap "Open" below to install the app. ' +
          'After install, open the app and allow paste to continue your signup.',
        imageUrl: 'https://fitillion.com/wp-content/uploads/2022/02/studio-experts.jpg',
      },
    });
  }

  public async createSpecialOfferLink(uid: string): Promise<string> {
    const url = `${this.platformUrl}/api/payment-router/special-offer?userId=${uid}`;
    return this.createShortlinkWebFallback(url);
  }

  public async createMembershipPlansLink(gymName: string, email: string): Promise<string> {
    const url = `${this.platformUrl}/membership-plans?gymName=${gymName}&e=${email}`;
    return this.createShortlinkWebFallback(url);
  }

  private analyticsTagsFromInfluencerSettings(influencerSettings?: IInfluencerSettings) {
    const googlePlayAnalytics = influencerSettings && influencerSettings.utmTags ? {
      googlePlayAnalytics: {
        utmSource: influencerSettings.utmTags.source,
        utmMedium: influencerSettings.utmTags.medium,
        utmCampaign: influencerSettings.utmTags.campaign,
      },
    } : {};

    const influencerSocialMetaTagInfo = influencerSettings && influencerSettings.socialMetaTags ? {
      socialTitle: influencerSettings.socialMetaTags.title,
      socialDescription: influencerSettings.socialMetaTags.description,
      socialImageLink: influencerSettings.socialMetaTags.imageUrl,
    } : {};

    return { googlePlayAnalytics, influencerSocialMetaTagInfo };
  }
}
